import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { useResultHandler } from "../../../hooks/useResultHandler";
import { getNewsDetail } from "../api/news.api";
import type { NewsDetailResponse } from "../types/news.types";

interface UseGetNewsDetailReturn {
  newsDetail: NewsDetailResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<NewsDetailResponse | null>;
}

export const useGetNewsDetail = (
  newsId?: string,
  initialNews?: NewsDetailResponse | null,
): UseGetNewsDetailReturn => {
  const [newsDetail, setNewsDetail] = useState<NewsDetailResponse | null>(initialNews ?? null);
  const [error, setError] = useState<string | null>(null);
  const { run: runLatestRequest, cancel, loading } = useLatestRequest();
  const { handleError } = useResultHandler();

  const fetchNewsDetail = useCallback(async (): Promise<NewsDetailResponse | null> => {
    if (!newsId) return null;

    if (initialNews?.id === newsId) {
      setNewsDetail(initialNews);
      setError(null);
      return initialNews;
    }

    setNewsDetail(null);
    setError(null);

    try {
      const response = await runLatestRequest((signal) => getNewsDetail(newsId, signal));

      if (!response) return null;

      setNewsDetail(response);
      return response;
    } catch (requestError) {
      setError(handleError(requestError, "Failed to load news article"));
      return null;
    }
  }, [handleError, initialNews, newsId, runLatestRequest]);

  useEffect(() => {
    if (!newsId || initialNews?.id === newsId) {
      cancel();
      return;
    }

    void fetchNewsDetail();
  }, [cancel, fetchNewsDetail, initialNews, newsId]);

  return {
    newsDetail,
    loading,
    error,
    refetch: fetchNewsDetail,
  };
};
